import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaRegEye } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2';
import { IoArrowBack } from 'react-icons/io5';
import { PiFileText, PiPlant, PiSquaresFour, PiTractor } from 'react-icons/pi';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { BlockerFunction } from 'react-router-dom';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';

import { Dropdown, Modal, Segmented, Spin, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import Breadcrumb, { BreadcrumbRoute } from 'components/common/Breadcrumb';
import Button from 'components/common/Button';
import CopyableId from 'components/common/CopyableId';
import SavingStatusLabel from 'components/reusable/SavingStatusLabel';
import { NUTRITION_NOT_SAVED_MESSAGE } from 'constants/messageConstants';
import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  FARM_PLAN_PREVIEW_PAGE_PATH,
  FARMS_PAGE_PATH,
  FARMS_URL_PARAM_GROUP_BY,
  FARMS_URL_PARAM_NODE,
  FARMS_URL_PARAM_TAB,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  ORGANISATIONS_PAGE_PATH,
  PADDOCK_KEY_PREFIX,
  PADDOCK_PLAN_PREVIEW_PAGE_PATH,
} from 'constants/routeConstants';
import {
  ADD_PLAN_BUTTON_LABEL,
  AREA_UNIT,
  CHOOSE_PLAN_PROMPT_MESSAGE,
  FARM_ID_LABEL,
  FARM_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE,
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_TAB_DIAGNOSTICS,
  INPUT_DOC_TAB_INSIGHTS,
  INPUT_DOC_TAB_LABELS,
  INPUT_DOC_TAB_MAP,
  INPUT_DOC_TAB_PLANNING,
  INPUT_ID_LABEL,
  INPUT_WORKSPACE_PLACEHOLDER_MESSAGE,
  NO_PLAN_AVAILABLE_MESSAGE,
  NUTRITION_SAVED_TOOLTIP,
  ORG_ID_LABEL,
  PADDOCK_ID_LABEL,
  PADDOCK_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE,
  PREVIEW_BUTTON_LABEL,
  PREVIEW_MENU_ALL_PADDOCKS_LABEL,
  PREVIEW_MENU_THIS_PADDOCK_LABEL,
  RETURN_TO_ORGANISATION_LIST_LABEL,
  SAVED_BY_UNKNOWN_LABEL,
  UNSAVED_CHANGES_DISCARD_LABEL,
  UNSAVED_CHANGES_KEEP_EDITING_LABEL,
  UNSAVED_CHANGES_LEAVE_PAGE_MESSAGE,
  UNSAVED_CHANGES_MODAL_TITLE,
} from 'constants/uiConstants';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { useFetchZonesByOrgId } from 'hooks/useFetchZonesByOrgId';
import { useMOrgId } from 'hooks/useMOrgId';
import { useProductionTonneUnit } from 'hooks/useProductionTonneUnit';
import { useResolvedCropOption } from 'hooks/useResolvedCropOption';
import { useVisibleOrgData } from 'hooks/useVisibleOrgData';
import { IInput, ZoneLinkType } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import CreatePlanModal from 'pages/FarmsPage/CreatePlanModal';
import { buildFarmsLink } from 'pages/FarmsPage/hooks/farmsLinkTarget';
import { RootState } from 'redux/Store';
import { nutritionRetryRequested } from '../../../redux/slices/InsightsSlice';
import { writeDiagnosticsSnapshot } from 'pages/PlanPreview/diagnosticsSnapshot';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import {
  filterInputsByYear,
  getInputDisplayName,
  getInputsForPaddock,
  getInputVarietyName,
  getInputYear,
  groupInputsByCrop,
} from 'utils/FarmUtils';
import resolveUserTimeZone from 'utils/TimeZoneUtils';
import { toast } from 'utils/toast';
import { getUserDisplayName } from 'utils/UserUtils';

import MapView from '../Map';

import FarmDiagnosticsView from './FarmDiagnostics/FarmDiagnosticsView';
import InputAttributesForm from './InputAttributesForm';
import InputInsights from './Insights';
import { resolvePaddockId } from './Insights/computeLiveTotals';
import {
  deriveScopeLevel,
  type InsightsScopeLevel,
} from './Insights/insightUtils';
import OrganisationWorkspacePlaceholder from './OrganisationWorkspacePlaceholder';
import InputTasks from './Tasks';
import { ProductionCropContext } from './Tasks/stepFactory';
import { usePendingTaskSave } from './Tasks/usePendingTaskSave';
import { useInputDocumentDraft } from './useInputDocumentDraft';
import WorkspaceEmptyState from './WorkspaceEmptyState';

interface OrganisationWorkspaceProps {
  completionFlags?: boolean[];
  dataSizes?: number[];
}

const OrganisationWorkspace: React.FC<OrganisationWorkspaceProps> = ({
  completionFlags,
  dataSizes,
}) => {
  const selectedFarmId = useSelector(
    (state: RootState) => state.farmReducer.selectedFarmId
  );
  const selectedPaddockId = useSelector(
    (state: RootState) => state.farmReducer.selectedPaddockId
  );
  const activeTreeNodeKey = useSelector(
    (state: RootState) => state.farmReducer.activeTreeNodeKey
  );
  const isSwitchingOrganisation = useSelector(
    (state: RootState) =>
      state.activeOrganisationReducer.isSwitchingOrganisation
  );
  const isOrgLoading = isSwitchingOrganisation || !!completionFlags;
  const isDataReady = !completionFlags || completionFlags.every(Boolean);

  const { selectedInputId } = useSelector(
    (state: RootState) => state.inputReducer
  );

  const activeOrganisationId = useSelector(
    (state: RootState) => state.activeOrganisationReducer.organisationId
  );

  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );

  const isBackPaddockStaff = useSelector(
    (state: RootState) => state.staffReducer.isBackPaddockStaff
  );

  const { data: organisations = [] } = useFetchOrganisations();
  const activeOrganisation = useMemo(
    () => organisations.find((org) => org.id === activeOrganisationId),
    [organisations, activeOrganisationId]
  );

  // Excluded farms/paddocks and their plans are hidden everywhere (OP-2390).
  const { farms, inputs } = useVisibleOrgData(activeOrganisationId);
  const { data: zones = [] } = useFetchZonesByOrgId(activeOrganisationId);

  const selectedFarm = useMemo(
    () => farms.find((farm) => farm.id === selectedFarmId),
    [farms, selectedFarmId]
  );
  const selectedPaddock = useMemo(
    () =>
      selectedFarm?.paddocks.find(
        (paddock) => paddock.paddockId === selectedPaddockId
      ),
    [selectedFarm, selectedPaddockId]
  );

  // Zone ID that corresponds to the tree-selected paddock — highlights the
  // matching polygon on the map when the tree selection changes.
  const highlightedZoneId = selectedPaddock?.zoneId ?? null;

  const navigate = useNavigate();

  const selectedInput = useMemo(
    () => inputs.find((input) => input.id === selectedInputId),
    [inputs, selectedInputId]
  );

  // The selected paddock's own plans for the active year — used to tell
  // apart "no plan yet" from "more than one, ask which" when no specific
  // plan is open (useFarmsNavSync only auto-opens one when it's the sole
  // plan; anything else is left for the Planning tab to resolve itself).
  const selectedPaddockPlans = useMemo(() => {
    if (!selectedPaddock) return [];
    return filterInputsByYear(
      getInputsForPaddock(inputs, selectedPaddock.paddockId),
      treeYearFilter
    );
  }, [inputs, selectedPaddock, treeYearFilter]);

  const { data: userProfile } = useFetchUserProfile();

  const savedBy = useMemo(() => {
    const fallbackName = getUserDisplayName(
      userProfile?.firstName,
      userProfile?.lastName
    );
    return (
      selectedInput?.snapModifier ||
      fallbackName ||
      SAVED_BY_UNKNOWN_LABEL
    ).trim();
  }, [
    selectedInput?.snapModifier,
    userProfile?.firstName,
    userProfile?.lastName,
  ]);

  const userTimeZone = useMemo(
    () => resolveUserTimeZone(userProfile?.state, userProfile?.country),
    [userProfile?.country, userProfile?.state]
  );

  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );

  const gOrgId = activeGOrganisation || null;

  const mOrgId = useMOrgId();

  const {
    draft,
    selectedCropOption,
    varietyOptions,
    unitValue,
    mergedCrops,
    gUnits,
    isCropsLoading,
    isCropsPaginating,
    hasNextCropPage,
    fetchNextCropPage,
    cropSearchTerm,
    onCropSearch,
    hasUnsavedHeaderChanges,
    lastErrorMessage,
    hasFailedUpdate,
    isRetrying,
    headerLastSavedAt,
    isDraftValid,
    headerChangeCount,
    retryLastHeaderUpdate,
    saveHeaderNow,
    discardChanges,
    handleCropChange,
    handleVarietyChange,
    handleStartDateChange,
    handleEndDateChange,
    handleZoneAreaChange,
    handleUnitChange,
    addDraftTask,
    updateDraftTask,
    removeDraftTask,
  } = useInputDocumentDraft({
    selectedInput,
    activeOrganisationId,
    userProfile,
    gOrgId,
    mOrgId,
  });

  const {
    cropOption: productionCropOption,
    isResolving: isProductionCropResolving,
  } = useResolvedCropOption({
    cropId: draft?.crop?.globalId ?? null,
    mOrgId,
    loadedOption: selectedCropOption ?? null,
    isCatalogueLoading: isCropsLoading,
  });

  const {
    tonneUnit: productionTonneUnit,
    isLoading: isProductionTonneUnitLoading,
  } = useProductionTonneUnit(gOrgId);

  const productionCropContext = useMemo(
    (): ProductionCropContext => ({
      crop: draft?.crop ?? null,
      cropOption: productionCropOption,
      tonneUnit: productionTonneUnit,
      isResolving: isProductionCropResolving || isProductionTonneUnitLoading,
    }),
    [
      draft?.crop,
      isProductionCropResolving,
      isProductionTonneUnitLoading,
      productionCropOption,
      productionTonneUnit,
    ]
  );

  const {
    hasPendingTaskChanges,
    pendingTaskCount,
    lastSavedAt: lastTaskSavedAt,
    schedulePendingTaskSave,
    schedulePendingNewTaskSave,
    schedulePendingTaskDelete,
    flushPendingTaskSaves,
    cancelAllPendingTaskSaves,
  } = usePendingTaskSave({
    inputId: selectedInput?.id ?? '',
    activeOrganisationId,
  });

  const handleInputSave = useCallback(() => {
    saveHeaderNow();
    flushPendingTaskSaves();
  }, [flushPendingTaskSaves, saveHeaderNow]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Called when the user clicks a polygon on the map. Switches to Farm view
  // and selects the corresponding paddock node via URL params.
  const handlePaddockSelect = useCallback(
    (paddockId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(FARMS_URL_PARAM_GROUP_BY); // Farm view is the default
        next.set(FARMS_URL_PARAM_NODE, `${PADDOCK_KEY_PREFIX}${paddockId}`);
        return next;
      });
    },
    [setSearchParams]
  );

  // Choosing a plan from the multi-plan picker below — same URL-driven
  // selection mechanism as clicking the plan directly in the tree.
  const handleSelectPlan = useCallback(
    (inputId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(FARMS_URL_PARAM_NODE, `${INPUT_KEY_PREFIX}${inputId}`);
        return next;
      });
    },
    [setSearchParams]
  );

  // "Create a plan" CTA shown when a selected paddock has no plan for the
  // active year (see the Planning-tab ghost state below) — mirrors the same
  // trigger pattern FarmTreeView uses for its own "+ Plan" ghost row: local
  // state, modal mounted only while open.
  const [paddockForNewPlan, setPaddockForNewPlan] = useState<IPaddock | null>(
    null
  );

  const handlePlanCreated = useCallback(
    (createdInput: IInput) => {
      setPaddockForNewPlan(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(FARMS_URL_PARAM_NODE, `${INPUT_KEY_PREFIX}${createdInput.id}`);
        return next;
      });
    },
    [setSearchParams]
  );

  const activeTab = useSelector(
    (state: RootState) => state.insightsReducer.activeTab
  );
  const dispatch = useDispatch();
  const activeLens = useSelector(
    (state: RootState) => state.insightsReducer.activeLens
  );
  const nutritionSave = useSelector(
    (state: RootState) => state.insightsReducer.nutritionSave
  );

  const hasUnsavedChanges = hasUnsavedHeaderChanges || hasPendingTaskChanges;
  const combinedLastSavedAt =
    Math.max(headerLastSavedAt ?? 0, lastTaskSavedAt ?? 0) || null;

  // On Insights → Nutrition the status label speaks for Nutrition's rate
  // changes, which save straight away: Saving…, Saved (no time — they go to
  // several plans), or an error listing what didn't save, with Retry. Same
  // label as Planning so the messages match. The open plan's own unsaved
  // changes or errors still take priority, so nothing pending in Planning is
  // hidden. Hidden until the user changes something on this visit.
  const showsNutritionSave =
    activeTab === INPUT_DOC_TAB_INSIGHTS &&
    activeLens === INPUT_DOC_LENS_NUTRITION &&
    !hasUnsavedChanges &&
    !lastErrorMessage &&
    !hasFailedUpdate &&
    !isRetrying &&
    (nutritionSave.pendingCount > 0 ||
      nutritionSave.hasSaved ||
      nutritionSave.failed.length > 0);

  // Prevent intermediate partial counts while saves are in flight.
  // Tracks the highest count seen and only resets to 0 once all changes are
  // gone — so both auto-debounce and explicit Save show "N changes → saved"
  // rather than "N → N-1 → ... → saved".
  const stableUnsavedCountRef = useRef(0);
  const currentUnsavedCount =
    (hasUnsavedHeaderChanges ? headerChangeCount : 0) + pendingTaskCount;
  stableUnsavedCountRef.current =
    currentUnsavedCount === 0
      ? 0
      : Math.max(stableUnsavedCountRef.current, currentUnsavedCount);

  // Scopes a candidate input list down to whichever tree node is active
  // (org / farm / paddock / crop / input) — factored out of scopedInputs so
  // the same node-scoping can run over either the tree's year-filtered list
  // (every other Insights lens) or the unfiltered list (Cashflow — see
  // cashflowScopedInputs below).
  const scopeInputsToTreeNode = useCallback(
    (candidateInputs: typeof inputs) => {
      if (!activeTreeNodeKey) return candidateInputs;

      if (activeTreeNodeKey.startsWith(INPUT_KEY_PREFIX)) {
        const inputId = activeTreeNodeKey.replace(INPUT_KEY_PREFIX, '');
        if (draft && draft.id === inputId) return [draft];
        const found = inputs.find((i) => i.id === inputId);
        return found ? [found] : [];
      }

      if (activeTreeNodeKey.startsWith(PADDOCK_KEY_PREFIX)) {
        const paddockId = activeTreeNodeKey.replace(PADDOCK_KEY_PREFIX, '');
        return candidateInputs.filter((input) =>
          input.zone?.links?.some(
            (l) => l.linkType === ZoneLinkType.Paddock && l.linkId === paddockId
          )
        );
      }

      if (activeTreeNodeKey.startsWith(FARM_KEY_PREFIX)) {
        const farmId = activeTreeNodeKey.replace(FARM_KEY_PREFIX, '');
        const farm = farms.find((f) => f.id === farmId);
        if (!farm) return [];
        const paddockIds = new Set(farm.paddocks.map((p) => p.paddockId));
        return candidateInputs.filter((input) =>
          input.zone?.links?.some(
            (l) =>
              l.linkType === ZoneLinkType.Paddock && paddockIds.has(l.linkId)
          )
        );
      }

      if (activeTreeNodeKey.startsWith(ORG_KEY_PREFIX)) {
        return candidateInputs;
      }

      // A crop node scopes to every plan of that crop, the same way a farm node
      // scopes to that farm's plans. The group is looked up by key rather than by
      // re-deriving one from the label, so this stays in step with how the tree
      // mints crop keys (including the no-crop bucket's sentinel).
      if (activeTreeNodeKey.startsWith(CROP_KEY_PREFIX)) {
        const cropKey = activeTreeNodeKey.replace(CROP_KEY_PREFIX, '');
        return (
          groupInputsByCrop(candidateInputs).find(
            (group) => group.cropKey === cropKey
          )?.inputs ?? []
        );
      }

      return [];
    },
    [activeTreeNodeKey, inputs, farms, draft]
  );

  const scopedInputs = useMemo(
    // For aggregate scopes (org / farm / paddock) apply the same year filter the
    // tree uses, so Insights totals only cover the plans that are visible.
    // For an INPUT node the user has explicitly opened that specific document —
    // FarmTreeView resets the selection if the plan is hidden by the year filter,
    // so no filtering is needed here (scopeInputsToTreeNode's INPUT_KEY_PREFIX
    // branch reads straight from `inputs`, ignoring its candidateInputs arg).
    () => scopeInputsToTreeNode(filterInputsByYear(inputs, treeYearFilter)),
    [scopeInputsToTreeNode, inputs, treeYearFilter]
  );

  // Cashflow needs plans across a calendar-year boundary for its own
  // Financial Year windowing (Jul-Jun) to work correctly. The tree's own
  // year filter only matches a plan's startDate against a single calendar
  // year (filterInputsByYear/getInputYear), which would incorrectly exclude
  // a plan that legitimately falls inside a selected financial year but
  // happens to be tagged with the other calendar year — e.g. FY2025
  // (Jul 2025-Jun 2026): a plan with a Feb 2026 startDate would be dropped
  // by a "2025" tree filter even though February is inside that FY.
  // computeCashflow does its own correct per-step year-window matching
  // (including a startDate fallback for undated steps — see its comments),
  // so it's given the tree-node scope without the tree's year filter
  // applied, and reads which year to center on from treeYearFilter directly.
  const cashflowScopedInputs = useMemo(
    () => scopeInputsToTreeNode(inputs),
    [scopeInputsToTreeNode, inputs]
  );

  // Name of whichever tree level is currently in scope — paddock name at
  // paddock/input scope (an input always belongs to one paddock), farm name
  // at farm scope, org name at org scope. Mirrors scopedInputs' own
  // key-prefix parsing rather than trusting selectedFarmId/selectedPaddockId,
  // which can go stale once the user climbs back up the tree.
  const scopeName = useMemo(() => {
    if (!activeTreeNodeKey) return undefined;

    if (activeTreeNodeKey.startsWith(FARM_KEY_PREFIX)) {
      const farmId = activeTreeNodeKey.replace(FARM_KEY_PREFIX, '');
      return farms.find((f) => f.id === farmId)?.farmName;
    }

    if (activeTreeNodeKey.startsWith(ORG_KEY_PREFIX)) {
      return activeOrganisation?.orgName;
    }

    if (activeTreeNodeKey.startsWith(CROP_KEY_PREFIX)) {
      const cropKey = activeTreeNodeKey.replace(CROP_KEY_PREFIX, '');
      return groupInputsByCrop(inputs).find(
        (group) => group.cropKey === cropKey
      )?.cropName;
    }

    // Paddock or Input node — both scoped to a single paddock.
    return selectedPaddock?.paddockName;
  }, [activeTreeNodeKey, farms, activeOrganisation, selectedPaddock, inputs]);

  // Which tree level the current scope resolves to — same prefix parsing as
  // scopeName/scopedInputs above, surfaced as a discrete value so a lens can
  // gate features that only make sense at a narrow-enough scope (e.g.
  // Summary 2's per-crop spend timeline).
  const scopeLevel: InsightsScopeLevel | undefined = useMemo(
    () =>
      deriveScopeLevel(activeTreeNodeKey, {
        input: INPUT_KEY_PREFIX,
        paddock: PADDOCK_KEY_PREFIX,
        farm: FARM_KEY_PREFIX,
        crop: CROP_KEY_PREFIX,
        org: ORG_KEY_PREFIX,
      }),
    [activeTreeNodeKey]
  );

  // Nutrition audits a paddock's history across seasons (OP-2508), so like
  // Cashflow it takes the tree-node scope without the year filter. A plan
  // node widens to every plan on that plan's paddock — the audit is the
  // paddock's story, not one plan's.
  const nutritionScopedInputs = useMemo(() => {
    if (scopeLevel !== 'input') return cashflowScopedInputs;
    const paddockId = cashflowScopedInputs[0]
      ? resolvePaddockId(cashflowScopedInputs[0])
      : null;
    return paddockId
      ? getInputsForPaddock(inputs, paddockId)
      : cashflowScopedInputs;
  }, [scopeLevel, cashflowScopedInputs, inputs]);

  // A boolean would block same-pathname setSearchParams calls too (OP-2260).
  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedChanges]
  );
  const blocker = useBlocker(shouldBlockNavigation);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [timeSinceSave, setTimeSinceSave] = useState(0);

  const showStatusBar =
    showsNutritionSave ||
    !!lastErrorMessage ||
    hasFailedUpdate ||
    hasUnsavedChanges ||
    !!combinedLastSavedAt;

  useEffect(() => {
    if (!combinedLastSavedAt) {
      setTimeSinceSave(0);
      return;
    }

    const tick = () => {
      const seconds = Math.max(
        0,
        Math.floor((Date.now() - combinedLastSavedAt) / 1000)
      );
      setTimeSinceSave(seconds);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [combinedLastSavedAt]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowLeaveDialog(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Discard the BrowserStorage draft when the user closes or navigates away
  // from the page while unsaved changes (header or task) exist. This prevents
  // a stale draft from being restored on the next visit.
  useEffect(() => {
    const handlePageHide = () => {
      if (!hasUnsavedChanges || !selectedInput?.id) return;
      BrowserStorageUtils.removeEditedInput(selectedInput.id);
      BrowserStorageUtils.removeOriginalInput(selectedInput.id);
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('unload', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('unload', handlePageHide);
    };
  }, [hasUnsavedChanges, selectedInput?.id]);

  // Clean up BrowserStorage once all pending changes (header + task) have
  // been saved. localStorage.removeItem is a no-op if the key doesn't exist,
  // so redundant calls are harmless.
  useEffect(() => {
    if (!selectedInput?.id || hasUnsavedChanges) return;
    BrowserStorageUtils.removeEditedInput(selectedInput.id);
    BrowserStorageUtils.removeOriginalInput(selectedInput.id);
  }, [hasUnsavedChanges, selectedInput?.id]);

  const paddockArea = useMemo(() => {
    if (!selectedPaddock) return '';
    const area = selectedPaddock.arableArea ?? selectedPaddock.paddockArea;
    if (!area) return '';
    return `${area} ${selectedPaddock.areaUnit?.unit || AREA_UNIT}`;
  }, [selectedPaddock]);

  const breadcrumbRoutes: BreadcrumbRoute[] = useMemo(() => {
    const truncate = (str: string, max = 20): string =>
      str.length > max ? `${str.slice(0, max)}…` : str;

    const routes: BreadcrumbRoute[] = [];

    routes.push({
      path: ORGANISATIONS_PAGE_PATH,
      breadcrumbName: '',
      clickable: false,
    });

    if (activeOrganisation) {
      routes.push({
        path: ORGANISATIONS_PAGE_PATH,
        breadcrumbName: truncate(activeOrganisation.orgName),
        isActive: false,
        clickable: !isOrgLoading,
        icon: <HiOutlineBuildingOffice2 size={14} className="text-gray-muted" />,
        tooltip: (
          <span className="flex flex-col gap-1">
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-none bg-transparent p-0 text-xs text-white hover:underline"
              onClick={() => navigate(ORGANISATIONS_PAGE_PATH)}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-charcoal">
                <IoArrowBack size={12} />
              </span>
              {RETURN_TO_ORGANISATION_LIST_LABEL}
            </button>
            {isBackPaddockStaff && (
              <CopyableId label={ORG_ID_LABEL} value={activeOrganisation.id} />
            )}
          </span>
        ),
      });
    }

    // Farm, crop and paddock crumbs double as the scope control (OP-2390):
    // a crumb above the current level moves the tree selection up to it,
    // keeping the tab, lens and year. The org crumb keeps its own behaviour
    // (back to the organisation list).
    const isFarmScope = !!activeTreeNodeKey?.startsWith(FARM_KEY_PREFIX);
    const isPaddockScope = !!activeTreeNodeKey?.startsWith(PADDOCK_KEY_PREFIX);
    // Full name when truncated, plus a staff-only "Copy … ID" control.
    const crumbTooltip = (
      name: string,
      idLabel: string,
      id: string
    ): React.ReactNode => {
      const showName = name.length > 20;
      if (!showName && !isBackPaddockStaff) return undefined;
      return (
        <span className="flex flex-col gap-1">
          {showName && <span>{name}</span>}
          {isBackPaddockStaff && <CopyableId label={idLabel} value={id} />}
        </span>
      );
    };
    const linkToNode = (nodeKey: string): string => {
      const target = buildFarmsLink(FARMS_PAGE_PATH, `?${searchParams}`, {
        nodeKey,
      });
      return `${target.pathname}${target.search}`;
    };

    if (activeTreeNodeKey?.startsWith(CROP_KEY_PREFIX) && scopeName) {
      routes.push({
        path: FARMS_PAGE_PATH,
        breadcrumbName: truncate(scopeName),
        isActive: true,
        icon: <PiPlant size={14} className="text-gray-medium" />,
        tooltip: scopeName.length > 20 ? scopeName : undefined,
      });
    }

    if (selectedFarm) {
      routes.push({
        path: linkToNode(`${FARM_KEY_PREFIX}${selectedFarm.id}`),
        breadcrumbName: truncate(selectedFarm.farmName),
        isActive: isFarmScope,
        icon: <PiTractor size={14} className="text-gray-muted" />,
        tooltip: crumbTooltip(
          selectedFarm.farmName,
          FARM_ID_LABEL,
          selectedFarm.id
        ),
      });
    }

    // The open plan is no longer a nested "Paddock Plan (area)" breadcrumb
    // crumb here — paddock name + area render as their own header inside the
    // Planning tab instead, below the breadcrumb (OP-2364).
    if (selectedPaddock) {
      routes.push({
        path: linkToNode(`${PADDOCK_KEY_PREFIX}${selectedPaddock.paddockId}`),
        breadcrumbName: truncate(selectedPaddock.paddockName),
        isActive: isPaddockScope,
        icon: <PiSquaresFour size={14} className="text-gray-muted" />,
        tooltip: crumbTooltip(
          selectedPaddock.paddockName,
          PADDOCK_ID_LABEL,
          selectedPaddock.paddockId
        ),
      });
    }

    return routes;
  }, [
    activeOrganisation,
    selectedFarm,
    selectedPaddock,
    isBackPaddockStaff,
    isSwitchingOrganisation,
    activeTreeNodeKey,
    scopeName,
    searchParams,
  ]);

  // Deliberately no 'noopener,noreferrer': passing it made Chrome return null
  // for a window it had actually opened, so the pop-up-blocked toast fired on
  // every click (the OP-2286 fix). Do not re-add it.
  const store = useStore<RootState>();

  // The Yearly Overview is session-only Redux, which the new tab starts
  // without. Written just before opening, because the new tab gets a copy of
  // sessionStorage as it stands at that moment (OP-2490). Read from the store
  // at click time rather than subscribed to, so typing rainfall doesn't
  // re-render the workspace.
  const openPreview = useCallback(
    (path: string, blockedMessage: string) => {
      if (activeOrganisationId) {
        writeDiagnosticsSnapshot(
          activeOrganisationId,
          store.getState().insightsReducer
        );
      }
      if (!window.open(path, '_blank')) {
        toast.error(blockedMessage);
      }
    },
    [activeOrganisationId, store]
  );

  // Both previews are driven by the open plan: the single-paddock one by its
  // id, the whole-organisation report by its year. No year picker — the year
  // comes from the plan the user triggered it from (OP-2171).
  //
  // An undated plan falls back to the year the tree is showing rather than to
  // TREE_YEAR_ALL: the all-years sentinel would quietly produce the largest
  // report the page can build, which is never what one undated plan implies.
  const previewMenuItems = useMemo<MenuProps['items']>(() => {
    if (!selectedInput) return [];

    return [
      {
        key: 'this-paddock',
        label: PREVIEW_MENU_THIS_PADDOCK_LABEL,
        onClick: () =>
          openPreview(
            `${PADDOCK_PLAN_PREVIEW_PAGE_PATH}/${selectedInput.id}`,
            PADDOCK_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE
          ),
      },
      {
        key: 'all-paddocks',
        label: PREVIEW_MENU_ALL_PADDOCKS_LABEL,
        onClick: () =>
          openPreview(
            `${FARM_PLAN_PREVIEW_PAGE_PATH}/${activeOrganisationId}/${
              getInputYear(selectedInput) ?? treeYearFilter
            }`,
            FARM_PLAN_PREVIEW_POPUP_BLOCKED_MESSAGE
          ),
      },
    ];
  }, [selectedInput, activeOrganisationId, treeYearFilter, openPreview]);

  const inputDocTabsOptions = useMemo(
    () => [
      {
        label: (
          <div className="w-20 text-center">
            <span
              className={
                activeTab === INPUT_DOC_TAB_PLANNING
                  ? 'mx-auto block w-[3.75rem] border-b-2 border-gray-dark pb-1 font-semibold'
                  : 'block pb-1 font-normal'
              }
            >
              {INPUT_DOC_TAB_LABELS[INPUT_DOC_TAB_PLANNING]}
            </span>
          </div>
        ),
        value: INPUT_DOC_TAB_PLANNING,
      },
      {
        label: (
          <div className="w-20 text-center">
            <span
              className={
                activeTab === INPUT_DOC_TAB_INSIGHTS
                  ? 'mx-auto block w-[3.75rem] border-b-2 border-gray-dark pb-1 font-semibold'
                  : 'block pb-1 font-normal'
              }
            >
              {INPUT_DOC_TAB_LABELS[INPUT_DOC_TAB_INSIGHTS]}
            </span>
          </div>
        ),
        value: INPUT_DOC_TAB_INSIGHTS,
      },
      {
        label: (
          <div className="w-auto whitespace-nowrap px-2 text-center">
            <span
              className={
                activeTab === INPUT_DOC_TAB_DIAGNOSTICS
                  ? 'mx-auto block border-b-2 border-gray-dark pb-1 font-semibold'
                  : 'block pb-1 font-normal'
              }
            >
              {INPUT_DOC_TAB_LABELS[INPUT_DOC_TAB_DIAGNOSTICS]}
            </span>
          </div>
        ),
        value: INPUT_DOC_TAB_DIAGNOSTICS,
      },
      ...(isBackPaddockStaff
        ? [
            {
              label: (
                <div className="w-20 text-center">
                  <span
                    className={
                      activeTab === INPUT_DOC_TAB_MAP
                        ? 'mx-auto block w-[3.75rem] border-b-2 border-gray-dark pb-1 font-semibold'
                        : 'block pb-1 font-normal'
                    }
                  >
                    {INPUT_DOC_TAB_LABELS[INPUT_DOC_TAB_MAP]}
                  </span>
                </div>
              ),
              value: INPUT_DOC_TAB_MAP,
            },
          ]
        : []),
    ],
    [activeTab, isBackPaddockStaff]
  );

  const handleInputDocTabsChange = (value: string): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === INPUT_DOC_TAB_INSIGHTS) {
        next.set(FARMS_URL_PARAM_TAB, INPUT_DOC_TAB_INSIGHTS);
      } else if (value === INPUT_DOC_TAB_DIAGNOSTICS) {
        next.set(FARMS_URL_PARAM_TAB, INPUT_DOC_TAB_DIAGNOSTICS);
      } else if (value === INPUT_DOC_TAB_MAP) {
        next.set(FARMS_URL_PARAM_TAB, INPUT_DOC_TAB_MAP);
      } else {
        next.delete(FARMS_URL_PARAM_TAB);
      }
      return next;
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4">
      {/* Tab switcher — now sits above the breadcrumb (OP-2364).
          Left-aligned rather than justify-between: the saving status panel is
          `fixed right-28 top-0 z-[20]` and overlaps this row’s right-hand end,
          which left the Preview control hidden behind it. Padding cannot clear
          it reliably (the panel is w-fit, so its width changes with its
          message) and raising z-index would only swap which one is obscured. */}
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <Segmented
          className="input-tab-segmented"
          disabled={isOrgLoading}
          options={inputDocTabsOptions}
          value={activeTab}
          onChange={(value) => handleInputDocTabsChange(value)}
        />
        {selectedInputId && selectedInput && (
          <Dropdown menu={{ items: previewMenuItems }} trigger={['click']}>
            {/* Dropdown clones its child and attaches a ref; the shared
                Button is a plain function component, so it is wrapped rather
                than handed over directly. */}
            <span className="inline-flex">
              <Button
                variant="secondary"
                size="sm"
                className="flex items-center gap-1"
              >
                <FaRegEye size={12} />
                {PREVIEW_BUTTON_LABEL}
              </Button>
            </span>
          </Dropdown>
        )}
      </div>

      {activeTab !== INPUT_DOC_TAB_MAP &&
        (isOrgLoading ? (
          <div className="flex items-center gap-2 py-1 text-gray-muted">
            <Spin size="small" />
          </div>
        ) : (
          <Breadcrumb routes={breadcrumbRoutes} separator=">" />
        ))}
      {showStatusBar && (
        <div className="fixed right-28 top-0 z-[20] flex h-16 w-full items-center justify-between bg-gray-bright px-4 py-3 md:mr-1 md:w-fit md:bg-white md:px-6">
          {showsNutritionSave ? (
            <SavingStatusLabel
              errorMessage={
                nutritionSave.failed.length > 0
                  ? NUTRITION_NOT_SAVED_MESSAGE(nutritionSave.failed)
                  : null
              }
              onRetry={() => dispatch(nutritionRetryRequested())}
              showSaving={nutritionSave.pendingCount > 0}
              showSaved={nutritionSave.hasSaved}
              savedNote={NUTRITION_SAVED_TOOLTIP}
            />
          ) : (
            <SavingStatusLabel
              errorMessage={lastErrorMessage}
              showRetrying={isRetrying}
              showRetry={hasFailedUpdate}
              onRetry={retryLastHeaderUpdate}
              showRevert={hasUnsavedHeaderChanges && !isDraftValid}
              onRevert={discardChanges}
              showSave={
                (hasUnsavedHeaderChanges && isDraftValid) ||
                hasPendingTaskChanges
              }
              onSave={handleInputSave}
              showSaved={!hasUnsavedChanges && !!combinedLastSavedAt}
              unsavedChanges={stableUnsavedCountRef.current}
              timeSinceSave={timeSinceSave}
              savedAt={combinedLastSavedAt}
              savedBy={savedBy}
              timeZone={userTimeZone}
            />
          )}
        </div>
      )}

      <div className="mt-2 flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Planning tab */}
        <div
          className={
            activeTab === INPUT_DOC_TAB_PLANNING
              ? 'flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden'
              : 'hidden'
          }
        >
          {/* Paddock name + area — under the breadcrumb, above the plan's
              own input fields (OP-2364). */}
          {selectedPaddock && (
            <Tooltip
              title={
                isBackPaddockStaff && selectedInput ? (
                  <CopyableId label={INPUT_ID_LABEL} value={selectedInput.id} />
                ) : null
              }
              styles={{ root: { maxWidth: 'none' } }}
            >
              <div className="mb-3 flex w-fit cursor-default items-center gap-2">
                <PiSquaresFour
                  size={14}
                  className="flex-shrink-0 text-gray-muted"
                />
                <span className="text-base font-semibold text-charcoal">
                  {selectedPaddock.paddockName}
                </span>
                {paddockArea && (
                  <span className="text-sm font-normal text-gray-muted">
                    ({paddockArea})
                  </span>
                )}
              </div>
            </Tooltip>
          )}

          {selectedInputId && selectedInput ? (
            <>
              <div>
                {draft && (
                  <InputAttributesForm
                    draft={draft}
                    selectedCropOption={selectedCropOption}
                    varietyOptions={varietyOptions}
                    unitValue={unitValue}
                    mergedCrops={mergedCrops}
                    gUnits={gUnits}
                    isCropsLoading={isCropsLoading}
                    isCropsPaginating={isCropsPaginating}
                    hasNextCropPage={hasNextCropPage}
                    cropSearchTerm={cropSearchTerm}
                    onLoadMoreCrops={fetchNextCropPage}
                    onCropChange={handleCropChange}
                    onCropSearch={onCropSearch}
                    onVarietyChange={handleVarietyChange}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onZoneAreaChange={handleZoneAreaChange}
                    onUnitChange={handleUnitChange}
                  />
                )}
              </div>

              <InputTasks
                selectedInput={selectedInput}
                draftTasks={draft?.tasks ?? []}
                draftZone={draft?.zone}
                gUnits={gUnits}
                productionCropContext={productionCropContext}
                userProfile={userProfile}
                addDraftTask={addDraftTask}
                updateDraftTask={updateDraftTask}
                removeDraftTask={removeDraftTask}
                schedulePendingTaskSave={schedulePendingTaskSave}
                schedulePendingNewTaskSave={schedulePendingNewTaskSave}
                schedulePendingTaskDelete={schedulePendingTaskDelete}
              />
            </>
          ) : selectedPaddock && selectedPaddockPlans.length > 1 ? (
            // Several plans for the active year — ambiguous which one to
            // open automatically (useFarmsNavSync only auto-selects when
            // there's exactly one), so ask rather than silently guessing.
            <WorkspaceEmptyState message={CHOOSE_PLAN_PROMPT_MESSAGE}>
              <div className="flex w-full flex-col gap-2">
                {selectedPaddockPlans.map((plan) => {
                  const year = getInputYear(plan);
                  const variety = getInputVarietyName(plan);
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      className="flex items-center gap-2 rounded-md border border-gray-argent bg-white px-3 py-2 text-left hover:border-success hover:bg-rowHover"
                      onClick={() => handleSelectPlan(plan.id)}
                    >
                      <PiFileText
                        size={14}
                        className="flex-shrink-0 text-gray-muted"
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium text-charcoal">
                          {getInputDisplayName(plan)}
                          {year !== null && (
                            <span className="ml-1 text-xs font-semibold text-gray-muted">
                              ({year})
                            </span>
                          )}
                        </span>
                        {variety && (
                          <span className="truncate text-xs font-medium text-gray-dark">
                            {variety}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </WorkspaceEmptyState>
          ) : selectedPaddock ? (
            // Paddock is in scope but has no plan for the active year —
            // useFarmsNavSync would have auto-selected one if it had exactly
            // one (see selectedPaddockPlans.length > 1 above for several).
            <WorkspaceEmptyState message={NO_PLAN_AVAILABLE_MESSAGE}>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-gray-argent bg-white px-3 py-2 text-left hover:border-success hover:bg-rowHover"
                onClick={() => setPaddockForNewPlan(selectedPaddock)}
              >
                <FaPlus size={12} className="flex-shrink-0 text-gray-muted" />
                <span className="font-medium text-charcoal">
                  {ADD_PLAN_BUTTON_LABEL}
                </span>
              </button>
            </WorkspaceEmptyState>
          ) : !isDataReady ? (
            <OrganisationWorkspacePlaceholder
              key={activeOrganisationId || 'none'}
              completionFlags={completionFlags}
              dataSizes={dataSizes}
            />
          ) : (
            // Tree selection is a farm or the org root — Planning needs a
            // paddock or plan in scope, not a broader aggregate level.
            <WorkspaceEmptyState
              message={INPUT_WORKSPACE_PLACEHOLDER_MESSAGE}
            />
          )}
        </div>

        {/* Insights tab */}
        {activeTab === INPUT_DOC_TAB_INSIGHTS && (
          <div className="flex min-h-0 flex-1 flex-col">
            {!isDataReady ? (
              <OrganisationWorkspacePlaceholder
                key={activeOrganisationId || 'none'}
                completionFlags={completionFlags}
                dataSizes={dataSizes}
              />
            ) : (
              <InputInsights
                inputs={scopedInputs}
                cashflowInputs={cashflowScopedInputs}
                nutritionInputs={nutritionScopedInputs}
                farms={farms}
                gUnits={gUnits}
                scopeName={scopeName}
                scopeLevel={scopeLevel}
              />
            )}
          </div>
        )}

        {/* Farm Diagnostics tab — org-wide inputs, not the tree's year-filtered scopedInputs, since this view spans every year the paddock has data for. */}
        {activeTab === INPUT_DOC_TAB_DIAGNOSTICS && (
          <div className="flex min-h-0 flex-1 flex-col">
            {!isDataReady ? (
              <OrganisationWorkspacePlaceholder
                key={activeOrganisationId || 'none'}
                completionFlags={completionFlags}
                dataSizes={dataSizes}
              />
            ) : (
              <FarmDiagnosticsView
                inputs={inputs}
                farms={farms}
                selectedFarmId={selectedFarmId}
                orgName={activeOrganisation?.orgName ?? ''}
                paddockId={selectedPaddock?.paddockId}
                paddockName={selectedPaddock?.paddockName}
                farmId={selectedFarm?.id}
                farmName={selectedFarm?.farmName}
                gUnits={gUnits}
              />
            )}
          </div>
        )}

        {/* Map tab — BPC staff only */}
        {activeTab === INPUT_DOC_TAB_MAP && isBackPaddockStaff && (
          <div className="flex min-h-0 flex-1 flex-col">
            {isOrgLoading ? (
              <OrganisationWorkspacePlaceholder
                key={activeOrganisationId || 'none'}
                completionFlags={completionFlags}
                dataSizes={dataSizes}
              />
            ) : (
              <MapView
                key={activeOrganisationId || 'none'}
                zones={zones}
                highlightedZoneId={highlightedZoneId}
                onPaddockSelect={handlePaddockSelect}
              />
            )}
          </div>
        )}
      </div>

      {paddockForNewPlan && (
        <CreatePlanModal
          open
          paddock={paddockForNewPlan}
          activeOrganisationId={activeOrganisationId}
          gOrgId={gOrgId}
          onClose={() => setPaddockForNewPlan(null)}
          onCreated={handlePlanCreated}
        />
      )}

      <Modal
        open={showLeaveDialog}
        title={UNSAVED_CHANGES_MODAL_TITLE}
        okText={UNSAVED_CHANGES_DISCARD_LABEL}
        cancelText={UNSAVED_CHANGES_KEEP_EDITING_LABEL}
        onOk={() => {
          discardChanges();
          cancelAllPendingTaskSaves();
          setShowLeaveDialog(false);
          blocker.proceed?.();
        }}
        onCancel={() => {
          setShowLeaveDialog(false);
          if (blocker.state === 'blocked') {
            blocker.reset?.();
          }
        }}
      >
        <p className="text-gray-dark">{UNSAVED_CHANGES_LEAVE_PAGE_MESSAGE}</p>
      </Modal>
    </div>
  );
};

export default OrganisationWorkspace;
